import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Suggestion } from "@edufadder/shared-types";
import { Chip, spacing } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

const TONE: Record<string, "blue" | "green" | "neutral"> = { new: "blue", accepted: "green", declined: "neutral", duplicate: "neutral" };

export function SuggestionsPage(): JSX.Element {
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [filter, setFilter] = useState<"new" | "all">("new");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function load(): void {
    api.listSuggestions().then(setItems).catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load suggestions."));
  }
  useEffect(load, []);

  async function review(id: string, status: "declined" | "duplicate"): Promise<void> {
    try {
      await api.reviewSuggestion(id, { status, review_note: notes[id] ?? "" });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that review.");
    }
  }

  const shown = (items ?? []).filter((i) => filter === "all" || i.status === "new");
  return (
    <div style={s.wide}>
      <h1 style={s.heading1}>Suggested edufads</h1>
      <p style={s.mutedText}>
        Ideas readers want EduFadder to research. Accepting one starts a new draft claim, which then goes through the normal
        review before anything is published. Readers see the status and your note on their dashboard.
      </p>
      <div style={{ display: "flex", gap: spacing(2), margin: `${spacing(4)} 0` }}>
        <button type="button" style={filter === "new" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setFilter("new")}>
          Waiting ({(items ?? []).filter((i) => i.status === "new").length})
        </button>
        <button type="button" style={filter === "all" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setFilter("all")}>
          All
        </button>
      </div>
      {error && <p style={s.errorText}>{error}</p>}
      {items !== null && shown.length === 0 && <p>Nothing waiting. 🎉</p>}
      {shown.map((sg) => (
        <div key={sg.id} style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: spacing(3), flexWrap: "wrap" }}>
            <strong style={{ fontSize: 16 }}>{sg.idea}</strong>
            <Chip tone={TONE[sg.status] ?? "neutral"}>{sg.status}</Chip>
          </div>
          {sg.where_seen && <p style={{ margin: `${spacing(2)} 0 0` }}>Seen in: {sg.where_seen}</p>}
          {sg.source_hint && <p style={{ margin: `${spacing(1)} 0 0` }}>Source hint: {sg.source_hint}</p>}
          <p style={{ ...s.mutedText, margin: `${spacing(1)} 0 0` }}>{new Date(sg.created_at).toLocaleString()}</p>
          {sg.status === "new" ? (
            <div style={{ marginTop: spacing(3) }}>
              <input
                style={s.input}
                placeholder="Note to the reader (optional, shown on their dashboard)"
                value={notes[sg.id] ?? ""}
                onChange={(e) => setNotes((n) => ({ ...n, [sg.id]: e.target.value }))}
              />
              <div style={{ display: "flex", gap: spacing(2), flexWrap: "wrap" }}>
                <Link to={`/claims/new?suggestion=${encodeURIComponent(sg.id)}&label=${encodeURIComponent(sg.idea)}`} style={s.buttonPrimaryLink}>
                  Accept &amp; start a draft claim
                </Link>
                <button type="button" style={s.buttonSecondary} onClick={() => void review(sg.id, "duplicate")}>
                  Already covered
                </button>
                <button type="button" style={s.buttonSecondary} onClick={() => void review(sg.id, "declined")}>
                  Decline
                </button>
              </div>
            </div>
          ) : (
            sg.review_note && <p style={{ margin: `${spacing(2)} 0 0` }}>Your note: {sg.review_note}</p>
          )}
        </div>
      ))}
    </div>
  );
}
